/* AZ & CO.

   Four jobs: make the paper, keep the bag, search the catalogue, and switch
   variants on a product page. Everything else is CSS.

   The bag is the important one. Shopify's checkout is closed to us on every
   plan, so the drawer is the last surface we control, and it is also what
   makes the layering page possible: adding a second bottle can no longer
   throw you off the page you are shopping. */

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

/* --------------------------------------------------------------- money */

function money (cents) {
  // One rule everywhere: "AED 660". The shop's own format renders
  // "Dhs. 440.00", which is neither what the design says nor what these
  // prices need, since none of them have decimals.
  const n = Math.round(cents / 100).toLocaleString('en-US');
  const label = (window.AZ && window.AZ.currency) || 'AED';
  return label + ' ' + n;
}

/* ---------------------------------------------------------------- paper

   Generated per session rather than shipped as an image: a stock noise
   texture reads as a stock noise texture. Fine speckle, then a horizontal
   blur that wraps at the tile edge so 220px repeats leave no seam. */

function makeGrain (size = 220) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(size, size);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = 128 + (Math.random() - 0.5) * 26;
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 16;
  }
  ctx.putImageData(img, 0, 0);
  // Set the variable the stylesheet expects, not backgroundImage directly:
  // .paper layers two gradients over the grain and only the grain repeats.
  // Assigning backgroundImage wiped both and left one untiled square in the
  // corner of the page.
  document.documentElement.style.setProperty('--grain', `url(${c.toDataURL()})`);
}

/* ---------------------------------------------------------------- reveal

   The hero lines sit translated below their own clipping mask until this
   marks them in. The old site did it and the port dropped it, which left the
   headline invisible: the single worst kind of bug, because the page looks
   deliberately empty rather than broken. */

function initReveal () {
  const sets = $$('.line-set, .hook-object');
  if (!sets.length) return;
  // a frame first, so the transition has a start state to move from
  requestAnimationFrame(() => {
    requestAnimationFrame(() => sets.forEach((el) => el.setAttribute('data-in', '')));
  });
}

/* ------------------------------------------------------------------ bag */

const Cart = {
  state: null,

  async get () {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    this.state = await r.json();
    return this.state;
  },

  /* Takes the form, not an id.

     The first version read a single hidden input and posted JSON. That broke
     the moment a form had anything else in it: the gift card carries the
     recipient's name, email, message and send date as line-item properties,
     and every one of them was silently dropped. Its amount is a set of radio
     buttons too, and querying [name=id] returned the first rather than the
     checked one, so every card added was AED 100 whatever you picked.

     FormData carries whatever the form actually holds. */
  async add (form) {
    const body = new FormData(form);
    const r = await fetch(window.AZ.routes.cart_add + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.description || 'That could not be added.');
    }
    return this.get();
  },

  async change (key, qty) {
    const r = await fetch(window.AZ.routes.cart_change + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    this.state = await r.json();
    return this.state;
  }
};

/* The drawer builds its lines from cart.js, so the two things the Liquid
   snippets do server-side have to be done again here or they simply do not
   happen in the bag. */

function esc (v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* Mirror of snippets/clean-title.liquid. Titles arrive as "Hacivat — Nishane"
   and the drawer already prints the house on the line above, so without this
   every bag line named the house twice.

   The comparison is folded rather than literal because the vendor field on
   this catalogue is a machine slug: "essential_parfums", "memo_paris",
   "etat_libre". A plain startsWith never matched any of them, so 49 bottles
   across 9 houses printed the house inside the name and again underneath it.
   Folding lowercases, drops accents and treats punctuation as a space, which
   also catches "Les Indémodables" against a vendor of "indemodables" — the
   article belongs to the house, so it goes with it. */

function foldName (s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function cleanTitle (title, vendor) {
  let t = String(title || '').trim();
  for (const sep of [' — ', ' – ', ' - ']) {
    if (t.includes(sep)) { t = t.split(sep)[0].trim(); break; }
  }

  const v = foldName(vendor);
  if (v) {
    const vWords = v.split(' ').length;
    const words = t.split(/\s+/);
    const article = ['les', 'le', 'la', 'the'].includes(foldName(words[0])) ? 1 : 0;
    for (const start of article ? [0, 1] : [0]) {
      if (foldName(words.slice(start, start + vWords).join(' ')) !== v) continue;
      const rest = words.slice(start + vWords).join(' ').trim();
      if (rest) t = rest;
      break;
    }
  }

  if (String(title || '').toLowerCase().includes('tester')) t += ' (Tester)';
  return t;
}

/* Adding a bottle changed a number in the corner and slid a panel in. Neither
   of those is announced, so on a screen reader the tap did nothing at all. */
function announce (msg) {
  const region = $('#cart-live');
  if (region) region.textContent = msg;
}

function paintCart (cart) {
  const lines = $('#cart-lines');
  const foot = $('#cart-foot');
  const empty = $('#cart-empty');
  const count = $('#bag-count');

  if (count) {
    count.textContent = cart.item_count;
    count.hidden = cart.item_count === 0;
  }
  if (!lines) return;

  if (!cart.items.length) {
    lines.innerHTML = '';
    if (foot) foot.hidden = true;
    if (empty) empty.hidden = false;
    return;
  }
  if (empty) empty.hidden = true;
  if (foot) foot.hidden = false;

  lines.innerHTML = cart.items.map((it) => `
    <div class="bag-line" data-key="${it.key}">
      <a class="bag-plate" href="${it.url}">
        ${it.image ? `<img src="${it.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')}"
          width="70" height="94" alt="" loading="lazy">` : ''}
      </a>
      <div class="bag-id">
        <p class="bag-house">${esc(it.vendor || '')}</p>
        <a class="bag-name" href="${it.url}">${esc(cleanTitle(it.product_title, it.vendor))}</a>
        ${it.variant_title && it.variant_title !== 'Default Title'
          ? `<p class="bag-variant">${esc(it.variant_title)}</p>` : ''}
        <button type="button" class="bag-remove" data-remove="${it.key}">Remove</button>
      </div>
      <p class="bag-price">${money(it.final_line_price)}</p>
    </div>`).join('');

  const total = $('#cart-total');
  if (total) total.textContent = money(cart.total_price);

  paintSaved(cart);
}

/* ------------------------------------------------------- saved vs retail

   cart.js does not carry compare_at_price. Its original_price field looks
   like the retail figure and is not: it only ever reflects Shopify's own
   cart discounts, so on this shop it equals price on every line and would
   have printed a saving of zero forever.

   The retail number lives on the product JSON instead, one request per
   distinct handle in the bag, cached for the life of the page. A bag holds
   one or two bottles, so this is a request or two, and the row simply stays
   hidden if any of it fails — a wrong saving is worse than no saving. */

const retailCache = new Map();

async function variantRetail (handle) {
  if (!retailCache.has(handle)) {
    retailCache.set(handle, fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        const map = new Map();
        if (p && p.variants) p.variants.forEach((v) => map.set(v.id, v.compare_at_price));
        return map;
      })
      .catch(() => new Map()));
  }
  return retailCache.get(handle);
}

async function paintSaved (cart) {
  const row = $('#cart-saved');
  const out = $('#cart-saved-value');
  if (!row || !out) return;

  try {
    const maps = await Promise.all([...new Set(cart.items.map((i) => i.handle))].map(variantRetail));
    const byHandle = new Map([...new Set(cart.items.map((i) => i.handle))].map((h, n) => [h, maps[n]]));

    let saved = 0;
    for (const it of cart.items) {
      const retail = (byHandle.get(it.handle) || new Map()).get(it.variant_id);
      // guard the known data problem: a few bottles carry a compare-at at or
      // below the selling price, and those must not subtract from the total
      if (retail && retail > it.final_price) saved += (retail - it.final_price) * it.quantity;
    }

    if (saved > 0) {
      out.textContent = money(saved);
      row.hidden = false;
    } else {
      row.hidden = true;
    }
  } catch {
    row.hidden = true;
  }
}

/* ------------------------------------------------------------- undo

   Removing is one tap beside a price on a phone, and the line above is
   another product. A confirm dialog would tax every correct removal to catch
   the occasional wrong one, and it is the kind of dialog people learn to
   dismiss without reading. This costs nothing unless it is needed.

   The window is deliberately generous: eight seconds is long enough to
   notice the bag is now wrong, which is the moment the mistake registers. */

let undoSnap = null;
let undoTimer = null;

function offerUndo (snap) {
  const bar = $('#bag-undo');
  const text = $('#bag-undo-text');
  if (!bar || !text) return;

  undoSnap = snap;
  text.textContent = snap.name + ' removed.';
  bar.hidden = false;
  announce(snap.name + ' removed from your bag.');

  clearTimeout(undoTimer);
  undoTimer = setTimeout(hideUndo, 8000);
}

function hideUndo () {
  const bar = $('#bag-undo');
  clearTimeout(undoTimer);
  undoSnap = null;
  if (bar) bar.hidden = true;
}

async function takeUndo () {
  if (!undoSnap) return;
  const snap = undoSnap;
  hideUndo();
  /* JSON rather than FormData here: the properties arrive as an object
     already, so there is no form to read and nothing to drop. */
  await fetch(window.AZ.routes.cart_add + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: snap.id, quantity: snap.quantity, properties: snap.properties }] })
  });
  paintCart(await Cart.get());
  announce(snap.name + ' put back.');
}

function openDrawer () {
  const d = $('#cart-drawer');
  if (!d) return;
  d.hidden = false;
  [...document.body.children].forEach((n) => { if (n !== d) n.setAttribute('inert', ''); });
  document.documentElement.style.overflow = 'hidden';
  const close = $('.drawer-close', d);
  if (close) close.focus();
}

function closeDrawer () {
  const d = $('#cart-drawer');
  if (!d) return;
  hideUndo();
  d.hidden = true;
  [...document.body.children].forEach((n) => n.removeAttribute('inert'));
  document.documentElement.style.overflow = '';
}

function initCart () {
  // every add-to-bag on the site posts through here instead of navigating
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button[type=submit]');
    const was = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding'; }
    try {
      const cart = await Cart.add(form);
      paintCart(cart);
      const last = cart.items[cart.items.length - 1];
      announce(last
        ? cleanTitle(last.product_title, last.vendor) + ' added to your bag. '
          + cart.item_count + (cart.item_count === 1 ? ' item.' : ' items.')
        : 'Added to your bag.');
      openDrawer();
      /* Anything that needs to know its own add landed — the gift form wants
         to confirm the recipient details it just captured, which the drawer
         cannot do because a line item does not show them. */
      document.dispatchEvent(new CustomEvent('az:added', { detail: { form, cart } }));
    } catch (err) {
      if (btn) btn.textContent = 'Ask on WhatsApp';
      console.error(err);
      return;
    } finally {
      if (btn && btn.textContent === 'Adding') { btn.disabled = false; btn.textContent = was; }
    }
    if (btn) { btn.disabled = false; btn.textContent = was; }
  });

  document.addEventListener('click', async (e) => {
    if (e.target.closest('#cart-open')) { await Cart.get().then(paintCart); return openDrawer(); }
    if (e.target.closest('[data-cart-close]')) return closeDrawer();
    const rm = e.target.closest('[data-remove]');
    if (rm) {
      /* Snapshot before it goes. cart.js hands back the variant, the
         quantity and the line properties, which is everything needed to put
         it back exactly — including a gift card's recipient name, number and
         send date, none of which anyone wants to type twice. */
      const key = rm.dataset.remove;
      const line = ((Cart.state && Cart.state.items) || []).find((i) => i.key === key);
      const snap = line && {
        id: line.variant_id,
        quantity: line.quantity,
        properties: line.properties || {},
        name: cleanTitle(line.product_title, line.vendor)
      };
      const cart = await Cart.change(key, 0);
      paintCart(cart);
      if (snap) offerUndo(snap);
    }
    if (e.target.closest('#bag-undo-do')) return takeUndo();
  });

  document.addEventListener('keydown', (e) => {
    const d = $('#cart-drawer');
    if (e.key === 'Escape' && d && !d.hidden) closeDrawer();
  });

  if (window.AZ && window.AZ.cart_count > 0) Cart.get().then(paintCart);
}

/* --------------------------------------------------------------- search

   Reads the storefront's own products.json, so it can never disagree with
   what is actually for sale. 250 is the page limit, so a catalogue this size
   is two requests, fetched once and kept. */

let catalogue = null;

async function loadCatalogue () {
  if (catalogue) return catalogue;
  const out = [];
  for (let page = 1; page <= 4; page++) {
    const r = await fetch(`/products.json?limit=250&page=${page}`);
    if (!r.ok) break;
    const { products } = await r.json();
    out.push(...products);
    if (products.length < 250) break;
  }
  catalogue = out;
  return out;
}

/* Placeholder rows shaped like real ones — same plate, same two lines — so
   the list does not jump when the results replace them. aria-hidden because
   a screen reader has nothing to gain from five empty rows; the list carries
   aria-busy instead, which is the part that actually means "wait". */
function skeletonRows (n) {
  return Array.from({ length: n }, () => `
    <li class="qs-row qs-row--skel" aria-hidden="true">
      <span>
        <span class="qs-skel-plate"></span>
        <span class="qs-skel-lines"><i></i><i></i></span>
      </span>
    </li>`).join('');
}

function initSearch () {
  const host = $('#quick-search');
  const openers = $$('#mast-search');
  if (!host || !openers.length) return;

  const input = $('#qs-input', host);
  const list = $('#qs-list', host);
  const hint = $('#qs-hint', host);
  let lastFocus = null;

  /* The houses, printed into the page by quick-search.liquid. Searching
     "xerjoff" used to return a handful of bottles and no way to ask for the
     rest of them; this puts the whole house one tap away. */
  let houses = [];
  try { houses = JSON.parse(($('#qs-houses') || {}).textContent || '[]'); } catch (e) { houses = []; }

  const open = async () => {
    lastFocus = document.activeElement;
    host.hidden = false;
    [...document.body.children].forEach((n) => { if (n !== host) n.setAttribute('inert', ''); });
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => input.focus(), 60);
    loadCatalogue();
  };
  const close = () => {
    host.hidden = true;
    [...document.body.children].forEach((n) => n.removeAttribute('inert'));
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };

  openers.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
  host.addEventListener('click', (e) => { if (e.target.closest('[data-qs-close]')) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !host.hidden) close(); });

  let t;
  input.addEventListener('input', () => {
    clearTimeout(t);
    t = setTimeout(async () => {
      const q = input.value.trim().toLowerCase();
      if (!q) { list.innerHTML = ''; hint.hidden = false; list.removeAttribute('aria-busy'); return; }
      hint.hidden = true;

      /* Only on the first query, when the two catalogue requests are still
         in the air. Afterwards the filter is synchronous and a skeleton
         would flash for a frame, which is worse than no skeleton at all. */
      if (!catalogue) {
        list.setAttribute('aria-busy', 'true');
        list.innerHTML = skeletonRows(5);
      }

      const data = await loadCatalogue();
      list.removeAttribute('aria-busy');

      /* The await above can outlast the query that started it. Without this
         a slow first request finishes after you have typed three more
         letters and paints results for a word you are no longer searching. */
      if (input.value.trim().toLowerCase() !== q) return;

      const hits = data.filter((p) =>
        (p.title + ' ' + p.vendor + ' ' + (p.tags || []).join(' ')).toLowerCase().includes(q)
      ).slice(0, 8);

      /* A house whose name contains the query, best match first: an exact
         name beats a prefix, a prefix beats a mention. Two at most, because
         this sits above the bottles and is a shortcut, not the answer. */
      const shelf = houses
        .filter((h) => h.t.toLowerCase().includes(q))
        .sort((a, b) => {
          const A = a.t.toLowerCase(), B = b.t.toLowerCase();
          return (A === q ? 0 : A.startsWith(q) ? 1 : 2) - (B === q ? 0 : B.startsWith(q) ? 1 : 2)
              || b.n - a.n;
        })
        .slice(0, 2);

      const shelfRows = shelf.map((h) => `<li class="qs-house">
        <a href="/collections/${h.h}">
          <span class="qs-house-t">Every ${esc(h.t)}</span>
          <span class="qs-house-n">${h.n} ${h.n === 1 ? 'bottle' : 'bottles'}</span>
        </a></li>`).join('');

      if (!hits.length) {
        list.innerHTML = shelfRows + `<li class="qs-empty">
          <p>We can get it.</p>
          <a class="btn btn--sm" href="https://wa.me/971509301032?text=${
            encodeURIComponent('Hi AZ & CO, I am looking for: ' + input.value.trim())
          }">Ask on WhatsApp</a></li>`;
        return;
      }

      list.innerHTML = shelfRows + hits.map((p) => {
        const v = p.variants[0];
        const img = p.images && p.images[0] ? p.images[0].src : '';
        const one = p.variants.length <= 1;
        return `<li class="qs-row">
          <a class="qs-open" href="/products/${p.handle}">
            ${img ? `<img src="${img.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2')}" width="56" height="75" alt="" loading="lazy">` : '<span></span>'}
            <span class="qs-id">
              <b>${esc(cleanTitle(p.title, p.vendor))}</b><i>${esc(p.vendor)}</i>
              <span class="qs-inline-price">${money(Math.round(parseFloat(v.price) * 100))}</span>
            </span>
            <span class="qs-price">${money(Math.round(parseFloat(v.price) * 100))}</span>
          </a>
          ${one
            ? `<form data-cart-add class="qs-add-form">
                 <input type="hidden" name="id" value="${v.id}">
                 <button type="submit" class="qs-add">Add</button>
               </form>`
            : `<a class="qs-add qs-add--pick" href="/products/${p.handle}">Choose</a>`}
        </li>`;
      }).join('');
    }, 110);
  });
}

/* -------------------------------------------------------- variant picker

   Price, saving, instalment, the concentration line and the hidden id all
   move together. Nothing on the page is allowed to disagree with the button. */

function initVariants () {
  const picker = $('#pdp-variants');
  if (!picker) return;

  picker.addEventListener('click', (e) => {
    const b = e.target.closest('.pick');
    if (!b) return;
    $$('.pick', picker).forEach((x) => {
      x.removeAttribute('data-on');
      x.setAttribute('aria-checked', 'false');
    });
    b.setAttribute('data-on', '');
    b.setAttribute('aria-checked', 'true');

    const price = Number(b.dataset.price);
    const compare = Number(b.dataset.compare);
    const id = $('#pdp-variant-id');
    if (id) id.value = b.dataset.variant;

    const now = $('#pdp-now');
    if (now) now.textContent = money(price);

    const kind = $('#pdp-kind');
    if (kind) kind.textContent = b.dataset.kind;

    const was = $('#pdp-was');
    const save = $('#pdp-save');
    /* Same floor the server applies. Without it, switching variant could
       resurrect a 1% "saving" that Liquid had deliberately withheld on load,
       so the page would contradict itself on a tap. */
    const floor = Number((save && save.dataset.floor) || 0);
    const pct = compare > price ? Math.round((compare - price) * 100 / compare) : 0;
    const has = compare > price && pct >= floor;
    if (was) {
      was.hidden = !has;
      if (has) was.textContent = money(compare);
    }
    if (save) {
      save.hidden = !has;
      if (has) {
        const mech = save.dataset.mech || '';
        save.textContent = `You save ${money(compare - price)}, ${pct}% under the boutique. ${mech}`;
      }
    }
    const cart = $('#pdp-cart');
    if (cart) cart.disabled = b.dataset.available !== 'true';
  });
}


/* ------------------------------------------------------------- layering

   A pair with no shared-notes line typed into the customiser works it out
   here: pull both descriptions, match them against the note vocabulary, and
   print the overlap. It means a new pair explains itself and nobody has to
   write that sentence again. */

const NOTES = ["agarwood", "almond", "amber", "ambergris", "ambroxan", "apple", "apricot", "aquatic", "basil", "benzoin", "bergamot", "caramel", "cardamom", "cashmeran", "cedar", "cherry", "chocolate", "cinnamon", "clove", "coconut", "coffee", "cypriol", "fig", "frankincense", "fresh", "geranium", "ginger", "grapefruit", "green", "honey", "incense", "iris", "jasmine", "labdanum", "lavender", "leather", "lemon", "lime", "mandarin", "marine", "mint", "musk", "myrrh", "neroli", "nutmeg", "oakmoss", "orange", "oud", "patchouli", "peach", "pear", "pepper", "petitgrain", "pineapple", "pistachio", "plum", "raspberry", "rose", "saffron", "sage", "salt", "sandalwood", "strawberry", "styrax", "suede", "tea", "thyme", "tobacco", "tonka", "tuberose", "vanilla", "vetiver", "violet", "ylang"];

function notesOf (product) {
  const text = ((product.body_html || '') + ' ' + product.title).toLowerCase();
  // Split into whole words rather than build a regex. The word-boundary
  // escape collapsed to a literal backspace character on the way through
  // the build, so this matched nothing at all and every pair stayed silent.
  const words = new Set(text.split(/[^a-z]+/));
  return NOTES.filter((n) => words.has(n));
}

async function initLayering () {
  const combos = $$('.combo[data-a][data-b]');
  if (!combos.length) return;
  const data = await loadCatalogue();
  const byHandle = new Map(data.map((p) => [p.handle, p]));

  combos.forEach((c) => {
    const line = $('[data-shared]', c);
    if (!line || !line.hidden) return;       // merchant typed one; leave it
    const a = byHandle.get(c.dataset.a);
    const b = byHandle.get(c.dataset.b);
    if (!a || !b) return;
    const set = new Set(notesOf(b));
    const both = notesOf(a).filter((n) => set.has(n)).slice(0, 4);
    if (!both.length) return;
    const em = $('em', line);
    if (em) em.textContent = both.join(' · ');
    line.hidden = false;
  });
}

/* ------------------------------------------------------------- spotlight

   The slides are printed by Liquid with their discount on a data attribute.
   All this does is put the deepest first and run the crossfade, so the hero
   costs one sorted array rather than a catalogue download.

   One bottle evaporates while the next condenses out of the paper: the
   outgoing lifts and blurs away, the incoming is revealed through a gradient
   mask sweeping down, and the bottle in frame drifts the whole time it is
   there. A slideshow that sits motionless between transitions reads as
   waiting. */

function initSpotlight () {
  const host = $('#spotlight');
  if (!host) return;

  const slides = $$('.spot', host);
  if (!slides.length) { host.hidden = true; return; }

  /* Deepest discount leads — unless the hero names its bottles, in which
     case the order in the markup is a decision and re-sorting would quietly
     overrule it. */
  if (!host.hasAttribute('data-fixed')) {
    slides
      .slice()
      .sort((a, b) => Number(b.dataset.off || 0) - Number(a.dataset.off || 0))
      .forEach((el) => host.appendChild(el));
  }

  const order = $$('.spot', host);
  order.forEach((el, i) => {
    el.toggleAttribute('data-on', i === 0);
    el.tabIndex = i === 0 ? 0 : -1;
    if (i === 0) el.removeAttribute('aria-hidden');
    else el.setAttribute('aria-hidden', 'true');
  });

  if (order.length < 2) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let at = 0;
  let timer = null;

  const show = (n) => {
    const prev = order[at];
    at = n % order.length;
    const next = order[at];
    prev.removeAttribute('data-on');
    prev.setAttribute('data-out', '');
    prev.setAttribute('aria-hidden', 'true');
    prev.tabIndex = -1;
    setTimeout(() => prev.removeAttribute('data-out'), 1200);
    next.setAttribute('data-on', '');
    next.removeAttribute('aria-hidden');
    next.tabIndex = 0;
  };

  // never run the clock against a hidden document: browsers throttle those
  // timers to about once a minute and it only banks up a jump
  const start = () => { if (!document.hidden) timer = timer || setInterval(() => show(at + 1), 5200); };
  const stop = () => { clearInterval(timer); timer = null; };

  host.addEventListener('pointerenter', stop);
  host.addEventListener('pointerleave', start);
  host.addEventListener('focusin', stop);
  host.addEventListener('focusout', start);
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
  start();
}

/* --------------------------------------------------------- sticky buy bar

   Shows only after the real Add to bag has left the screen. Two buy buttons
   visible at once is one buy button too many, and on a phone the decision is
   usually made three screens below the price. */

function initBuyBar () {
  const bar = $('#buybar');
  const anchorBtn = $('#pdp-cart');
  if (!bar || !anchorBtn) return;

  const io = new IntersectionObserver(([e]) => {
    bar.toggleAttribute('data-on', !e.isIntersecting && e.boundingClientRect.top < 0);
  }, { rootMargin: '0px' });
  io.observe(anchorBtn);
}

/* ------------------------------------------------------------- gift card

   Delivery is a choice, so only the fields that belong to it are shown and
   only those are required. Anything hidden is disabled as well as hidden:
   a hidden input still submits, and an empty Recipient email tells Shopify
   to email the card to nobody.

   The number is fixed to a UAE mobile. The +971 is printed beside the field
   rather than typed, the field accepts nine digits and nothing else, and the
   full number is composed into the hidden property on submit. Someone can
   neither paste an email into it nor send a half-finished number. */

/* --------------------------------------------------------- back to top

   Appears once the page has cost you something to scroll — a viewport and a
   half — and never on a page short enough not to need it.

   The listener is passive and does its work in a frame, because a scroll
   handler that reads layout on every event is the classic way to make a long
   list feel like it is dragging, which is the exact page this control exists
   for.

   Focus moves to the wordmark on arrival. Scrolling the window does not move
   the keyboard caret, so without this a keyboard user is returned to the top
   visually and left tabbing from the footer. */

function initToTop () {
  const btn = $('#to-top');
  if (!btn) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let ticking = false;
  let shown = false;

  const apply = () => {
    ticking = false;
    const past = scrollY > innerHeight * 1.5;
    const worth = document.documentElement.scrollHeight > innerHeight * 2.5;
    const want = past && worth;
    if (want === shown) return;
    shown = want;
    if (want) {
      btn.hidden = false;
      // a frame with display already set, so the transition has a start state
      requestAnimationFrame(() => btn.classList.add('is-in'));
    } else {
      btn.classList.remove('is-in');
      if (reduced) btn.hidden = true;
      else setTimeout(() => { if (!shown) btn.hidden = true; }, 450);
    }
  };

  addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(apply);
  }, { passive: true });

  btn.addEventListener('click', () => {
    scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    const mark = $('.mark');
    if (mark) mark.focus({ preventScroll: true });
  });

  apply();
}

/* -------------------------------------------------------------- theme

   The head script already applied any stored choice before first paint; this
   only handles the tap. Three states, not two: with nothing stored the page
   follows the system, and the first tap has to move away from whatever the
   system is currently giving rather than from a hardcoded "light".

   The label is rewritten each time because the button carries no text — an
   icon-only control whose accessible name still says "switch to dark" after
   you have switched is worse than no label. */

function initTheme () {
  const btn = $('#theme-toggle');
  if (!btn) return;

  const systemDark = () => matchMedia('(prefers-color-scheme: dark)').matches;
  const current = () => document.documentElement.getAttribute('data-theme')
    || (systemDark() ? 'dark' : 'light');

  const label = () => {
    btn.setAttribute('aria-label', current() === 'dark' ? 'Switch to light' : 'Switch to dark');
  };

  btn.addEventListener('click', () => {
    const next = current() === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('az-theme', next); } catch (e) {}
    label();
  });

  // a reader who never chose stays on the system, so follow it if it changes
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (!document.documentElement.getAttribute('data-theme')) label();
  });

  label();
}

/* ------------------------------------------------------- pair totals

   The "add both" line prints the two prices added up. Where a bottle has
   sizes the price moves with the select, so the figure has to follow it or
   it becomes a wrong number sitting next to a button — worse than no number.
   Prices ride on the options as data-price, so nothing is fetched. */

function initPairs () {
  const forms = $$('form[data-pair]');
  if (!forms.length) return;

  const paint = (form) => {
    const out = form.parentElement.querySelector('[data-pair-total]');
    if (!out) return;
    let total = 0;
    form.querySelectorAll('input[data-price]').forEach((i) => { total += Number(i.dataset.price) || 0; });
    form.querySelectorAll('select[data-pair-size]').forEach((sel) => {
      const opt = sel.selectedOptions[0];
      if (opt) total += Number(opt.dataset.price) || 0;
    });
    out.textContent = money(total);
  };

  forms.forEach((form) => {
    form.addEventListener('change', () => paint(form));
    paint(form);
  });
}

function initGift () {
  const form = $('[data-gift]');
  if (!form) return;

  const phone = $('#gift-phone', form);
  const phoneFull = $('#gift-phone-full', form);
  const auto = $('#gift-auto', form);

  const mode = () => (form.querySelector('[data-gift-mode]:checked') || {}).value || 'me';

  const apply = () => {
    const m = mode();
    $$('[data-gift-when]', form).forEach((el) => {
      const on = el.dataset.giftWhen.split(' ').includes(m);
      el.hidden = !on;
      el.querySelectorAll('input, textarea').forEach((f) => {
        f.disabled = !on;                       // hidden must also not submit
        if (f.type === 'tel' || f.type === 'email') f.required = on;
      });
    });
    // Shopify only auto-sends when it is an email address
    if (auto) auto.disabled = m !== 'email';
    if (auto) auto.value = m === 'email' ? 'on' : '';
    if (phoneFull) phoneFull.disabled = m !== 'whatsapp';
  };

  form.addEventListener('change', (e) => {
    if (e.target.matches('[data-gift-mode]')) apply();
  });

  /* --------------------------------------------------- what the amount buys

     Seven amounts and nothing to judge them against. Pricing real bottles
     against the chosen card turns an abstract number into a decision, and it
     surfaces the fact the amounts were hiding: the three smallest cards
     cannot buy anything in the shop on their own. */
  const panel = $('[data-gcard]', form);
  if (panel) {
    const items = $$('.gcard-item', panel);
    const floor = $('[data-gcard-floor]', panel);
    const shortTpl = panel.dataset.short || '[gap] short';

    const paint = () => {
      const picked = form.querySelector('input[name="id"]:checked');
      const amount = picked ? parseInt(picked.dataset.amount || '0', 10) : 0;
      let covered = 0;

      items.forEach((li) => {
        const price = parseInt(li.dataset.price || '0', 10);
        const ok = amount >= price;
        if (ok) covered += 1;
        li.dataset.covered = ok ? 'yes' : 'no';
        const tag = $('[data-gcard-state]', li);
        if (tag) {
          tag.textContent = ok
            ? (panel.dataset.covered || 'covered')
            : shortTpl.replace('[gap]', money(price - amount));
        }
      });

      // the honest line, shown only when the card really buys nothing
      if (floor) floor.hidden = covered > 0;
    };

    form.addEventListener('change', (e) => {
      if (e.target.name === 'id') paint();
    });
    paint();
  }

  if (phone) {
    // digits only, and never more than a UAE mobile has
    phone.addEventListener('input', () => {
      const clean = phone.value.replace(/\D/g, '').slice(0, 9);
      if (clean !== phone.value) phone.value = clean;
      if (phoneFull) phoneFull.value = clean ? '+971' + clean : '';
    });
  }

  /* ----------------------------------------------------------- validation

     The browser was doing this, and doing it in a bubble: a grey tooltip in
     the system font that vanishes on scroll, sits nowhere near the field on
     a phone, and says "Please match the requested format" about a number.
     novalidate is set from script, so anyone without JS keeps the native
     behaviour rather than losing validation altogether. */
  form.setAttribute('novalidate', '');

  const message = (f) => {
    if (f.validity.valueMissing) {
      if (f.type === 'tel') return 'Add their WhatsApp number.';
      if (f.type === 'email') return 'Add their email address.';
      return 'This one is needed.';
    }
    if (f.validity.typeMismatch && f.type === 'email') return 'That is not an email address.';
    if (f.validity.patternMismatch && f.type === 'tel') return 'A UAE mobile is nine digits starting with 5.';
    return 'Check this one.';
  };

  const clearError = (f) => {
    const field = f.closest('.gift-field');
    if (field) field.removeAttribute('data-invalid');
    f.removeAttribute('aria-invalid');
    const note = field && field.querySelector('.gift-err');
    if (note) note.remove();
  };

  const showError = (f) => {
    const field = f.closest('.gift-field');
    if (!field) return;
    clearError(f);
    field.setAttribute('data-invalid', '');
    f.setAttribute('aria-invalid', 'true');
    const note = document.createElement('p');
    note.className = 'gift-err';
    note.id = (f.id || f.name.replace(/\W+/g, '-')) + '-err';
    note.textContent = message(f);
    f.setAttribute('aria-describedby', note.id);
    field.appendChild(note);
  };

  // clearing as they fix it, rather than only on the next submit
  form.addEventListener('input', (e) => {
    if (e.target.matches('input, textarea') && e.target.validity.valid) clearError(e.target);
  });

  const validate = () => {
    const bad = [];
    $$('input, textarea', form).forEach((f) => {
      if (f.disabled || f.type === 'hidden') return;
      if (f.validity.valid) { clearError(f); return; }
      showError(f);
      bad.push(f);
    });
    return bad;
  };

  const done = $('#gift-done');

  form.addEventListener('submit', (e) => {
    if (phone && phoneFull) {
      const clean = phone.value.replace(/\D/g, '');
      phoneFull.value = clean ? '+971' + clean : '';
    }
    if (done) done.hidden = true;

    const bad = validate();
    if (bad.length) {
      /* Stop the delegated cart handler on document as well as the post.
         Without stopPropagation the bag would happily take a card addressed
         to nobody. */
      e.preventDefault();
      e.stopPropagation();
      bad[0].focus();
      bad[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    /* Optional fields left blank were still being submitted, so an order for
       someone who wrote no message arrived carrying an empty "Message"
       property — a blank line to read past on every one of them. Disabled
       inputs are not serialised, and apply() re-enables them on reset. */
    $$('[name^="properties["]', form).forEach((f) => {
      if (!f.disabled && !String(f.value).trim()) f.disabled = true;
    });
  });

  /* ------------------------------------------------------------- success

     The drawer opens on its own, but a line item cannot say who the card is
     for or when it goes, and those are the parts someone just typed and
     would like to see repeated back. */
  document.addEventListener('az:added', (e) => {
    if (e.detail.form !== form || !done) return;

    const m = mode();
    const amount = form.querySelector('[name=id]:checked');
    const label = amount ? amount.closest('.gift-chip').textContent.trim() : '';
    const who = (form.querySelector('[name="properties[Recipient name]"]') || {}).value || '';
    const when = (form.querySelector('[name="properties[Send on]"]') || {}).value || '';

    let where = 'It comes to you with the order.';
    if (m === 'whatsapp') where = 'We send the code to ' + (phoneFull.value || 'their number') + '.';
    if (m === 'email') where = 'It is emailed to them automatically.';

    /* The date field hands back 2026-09-12, which is a value, not a date
       anyone reads back to themselves. */
    let day = '';
    if (when) {
      const d = new Date(when + 'T00:00:00');
      if (!isNaN(d)) day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    }

    done.textContent = [
      label ? label + ' added.' : 'Added.',
      who ? 'For ' + who + '.' : '',
      where,
      day ? 'Going on ' + day + '.' : ''
    ].filter(Boolean).join(' ');
    done.hidden = false;

    form.reset();
    if (phoneFull) phoneFull.value = '';
    apply();
  });

  apply();
}

/* ---------------------------------------------------------------- start */

makeGrain();
/* ------------------------------------------------------------------- shelf

   A row that scrolls looks exactly like a row that does not. On a phone the
   shelf shows two bottles and an edge of a third, which reads as "we have
   three" rather than "there are twelve behind this".

   So the hint says how many, and the bar shows how far along you are. Both
   are driven from here rather than from a scroll-driven CSS animation: the
   animation version only ran on browsers that support scroll timelines and
   only when the visitor had not asked for reduced motion, so the people most
   likely to miss the affordance were the ones least likely to be shown it.
*/
/* The sort control submits on change, so choosing an option costs one tap
   instead of two. The form still works without this: the submit button is
   only hidden while JS is running, never removed. */
function initSort () {
  const sel = $('[data-sort]');
  if (!sel) return;
  const form = sel.form;
  if (form) form.classList.add('sortbar--live');
  sel.addEventListener('change', () => { if (form) form.submit(); });
}

function initRail () {
  const rail = $('#rail');
  if (!rail) return;
  const bar = $('.rail-bar i');
  const hint = $('#rail-hint');

  /* Telling somebody to scroll a row that already fits is worse than saying
     nothing, so the hint only exists while there is something to reach. */
  const fits = () => rail.scrollWidth <= rail.clientWidth + 4;

  const paint = () => {
    if (hint) hint.hidden = fits();
    if (fits()) { rail.removeAttribute('data-scrolled'); return; }

    const max = rail.scrollWidth - rail.clientWidth;
    const seen = (rail.scrollLeft + rail.clientWidth) / rail.scrollWidth;
    if (bar) bar.style.setProperty('--rail-progress', Math.max(0.08, Math.min(1, seen)).toFixed(3));

    // one nudge is enough; past this the row has explained itself
    if (rail.scrollLeft > 8) rail.setAttribute('data-scrolled', '');
    else rail.removeAttribute('data-scrolled');
  };

  rail.addEventListener('scroll', paint, { passive: true });
  addEventListener('resize', paint, { passive: true });
  paint();
}

initReveal();
initCart();
initSearch();
initVariants();
initBuyBar();
initLayering();
initSpotlight();
initGift();
initPairs();
initTheme();
initToTop();
initRail();
initSort();
